/* eslint-disable sort-keys */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { CallState } from '@lcs/shared/api';

import {
   RoleCallRecordingExtensionGroupInput,
   RoleCallRecordingExtensionInput,
   RoleInput,
} from 'generated/graphql-types';

import { DetailView } from 'src/app/common-components/detail/detail-view';

import { ExtensionOption } from '../../extension-register/models/extension-option.interface';

import { RoleReportViewModel } from '../../report-permissions-list/models/report-permission.model';

import { RolePrivileges } from '../../role-register/models/role-privileges';

import { RoleDetailSaveEvent } from '../models/role-detail-form-submit-event';

import { PrivilegeView, RoleDetailView } from '../models/role-detail-view.model';

import { Actions, ofType } from '@ngrx/effects';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { roleLogOutConfirmationPermissions } from 'src/app/common-components/user-logout-dialog/user-logout-dialog';
import { ExtensionGroupOption } from '../../extension-groups-register/models/extension-group-option.interface';
import { RoleDetailFormService } from '../services/role-detail-form.service';

import * as RoleDetailActions from '../state/role-detail.actions';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-role-detail-view',
   styleUrls: ['./role-detail-view.component.scss'],
   templateUrl: './role-detail-view.component.html',
   standalone: false,
})
export class RoleDetailViewComponent extends DetailView {
   @Input() callState: CallState;
   @Input() isOnSystemPage: boolean;

   @Input() get role(): RoleDetailView | null {
      return this._role;
   }

   set role(value: RoleDetailView | null) {
      this._role = value;

      if (value) {
         this._roleLoaded = true;
         this.updateForm();
      }
   }

   @Input() extensions: ExtensionOption[];

   @Input() extensionGroups: ExtensionGroupOption[];

   @Input() set rolePrivileges(privileges: RolePrivileges) {
      this._rolePrivileges = privileges;

      if (!privileges?.canEdit) {
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
      }
   }

   get rolePrivileges(): RolePrivileges {
      return this._rolePrivileges;
   }

   @Input() set privileges(privileges: PrivilegeView[] | null) {
      this._privileges = privileges;

      if (this._privileges) {
         this._privilegesLoaded = true;
         this.updateForm();
      }
   }
   loadSuccess$ = merge(
      this.actions$.pipe(ofType(RoleDetailActions.loadRoleDetailsSuccess)),
      this.actions$.pipe(ofType(RoleDetailActions.loadSystemRoleDetailsSuccess))
   );

   get privileges(): PrivilegeView[] | null {
      return this._privileges;
   }

   private _roleLoaded: boolean = false;
   private _privilegesLoaded: boolean = false;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<RoleDetailSaveEvent>();

   @Output() deleteRole = new EventEmitter<{
      roleId: number;
      name: string;
   }>();

   form: FormGroup;

   private _role: RoleDetailView | null;

   private _rolePrivileges: RolePrivileges;

   private _privileges: PrivilegeView[] | null;

   private destroyed$ = new Subject<void>();

   selectedIndex: number;

   reportsPermissionsChanged: boolean = false;

   constructor(
      public formService: RoleDetailFormService,
      private confirmationDialogService: ConfirmationDialogService,
      private actions$: Actions
   ) {
      super();
      this.form = this.formService.form;
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateForm(): void {
      if (this._roleLoaded && this._privilegesLoaded) {
         this.formService.updateRole(this._role as RoleDetailView);
         if (!this.rolePrivileges?.canEdit) {
            this.form.disable();
         } else if (this.rolePrivileges.canEdit) {
            this.form.enable();
         }
      }
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         if (submitEvent.rolePrivilegeFields.length > 0 || this.reportsPermissionsChanged) {
            roleLogOutConfirmationPermissions(this.confirmationDialogService)
               .afterClosed()
               .pipe(takeUntil(this.destroyed$))
               .subscribe((confirmed) => {
                  if (confirmed) {
                     this.formSubmitted.emit(submitEvent);
                  }
               });
         } else {
            this.formSubmitted.emit(submitEvent);
         }
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   onDeleteClick(): void {
      if (this.role) {
         this.deleteRole.emit({
            name: this.role.name ?? '',
            roleId: this.role.roleId,
         });
      }
   }

   handleReportsChange(reports: RoleReportViewModel[]): void {
      this.reportsPermissionsChanged = true;
      this.formService.form.get('roleReports')?.setValue(reports);
      this.formService.form.get('roleReports')?.markAsDirty();
   }

   private getFormSubmittedEvent(): RoleDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.role) {
         return null;
      }

      const role = this.formService.getValue(this.role.roleId);

      let roleFields: (keyof RoleInput)[] = [];

      roleFields = this.formService.addFieldForDirtyControl(roleFields, 'name');

      roleFields = this.formService.addFieldForDirtyControl(roleFields, 'description');

      let rolePrivilegeFields: string[] = [];

      if (role.rolePrivileges) {
         if (this.privileges) {
            this.privileges.forEach((privilege) => {
               rolePrivilegeFields = this.formService.addFieldForDirtyControl(
                  rolePrivilegeFields,
                  `rolePrivileges.${privilege.privilegeId}.isAdd`,
                  `${privilege.privilegeId}.isAdd`
               );

               rolePrivilegeFields = this.formService.addFieldForDirtyControl(
                  rolePrivilegeFields,
                  `rolePrivileges.${privilege.privilegeId}.isView`,
                  `${privilege.privilegeId}.isView`
               );

               rolePrivilegeFields = this.formService.addFieldForDirtyControl(
                  rolePrivilegeFields,
                  `rolePrivileges.${privilege.privilegeId}.isEdit`,
                  `${privilege.privilegeId}.isEdit`
               );

               rolePrivilegeFields = this.formService.addFieldForDirtyControl(
                  rolePrivilegeFields,
                  `rolePrivileges.${privilege.privilegeId}.isDelete`,
                  `${privilege.privilegeId}.isDelete`
               );

               rolePrivilegeFields = this.formService.addFieldForDirtyControl(
                  rolePrivilegeFields,
                  `rolePrivileges.${privilege.privilegeId}.isEnabled`,
                  `${privilege.privilegeId}.isEnabled`
               );
            });
         }
      }

      let roleCallRecordingExtensionFields: (keyof RoleCallRecordingExtensionInput)[] = [];

      roleCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionFields,
         'roleRecordingExtensions',
         'extensionId'
      );

      roleCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionFields,
         'roleRecordingExtensions',
         'isInclude'
      );

      roleCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionFields,
         'roleRecordingExtensions',
         'roleCallRecordingExtensionId'
      );

      roleCallRecordingExtensionFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionFields,
         'roleRecordingExtensions',
         'roleId'
      );

      roleFields = this.formService.addFieldForDirtyControl(
         roleFields,
         'roleRecordingExtensions',
         'hasAllExtensionRecordings'
      );

      let roleCallRecordingExtensionGroupFields: (keyof RoleCallRecordingExtensionGroupInput)[] = [];

      roleCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionGroupFields,
         'roleRecordingExtensions',
         'extensionGroupId'
      );

      roleCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionGroupFields,
         'roleRecordingExtensions',
         'isInclude'
      );

      roleCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionGroupFields,
         'roleRecordingExtensions',
         'roleCallRecordingExtensionGroupId'
      );

      roleCallRecordingExtensionGroupFields = this.formService.addFieldForDirtyControl(
         roleCallRecordingExtensionGroupFields,
         'roleRecordingExtensions',
         'roleId'
      );

      return {
         role,
         roleFields,
         rolePrivilegeFields,
         roleCallRecordingExtensionFields,
         roleCallRecordingExtensionGroupFields,
      };
   }
}
